import * as React from 'react'
import {
  Box,
  Dialog,
  DialogTitle,
  DialogActions,
  Typography,
  IconButton,
  Icon,
  DialogContent,
  SxProps,
} from '@design/pt-ui'
import ConfirmationDialog from '../components/ConfirmationDialog'
import { translateWithUserLocale } from '../utils/i18n'

interface ISimpleModalProps {
  id?: string
  activator?: any
  title: string | React.ReactNode
  children: any
  scroll?: 'body' | 'paper'
  open: boolean
  width?: string | number
  height?: string | number
  onOpen?: () => void
  onClose?: () => void
  confirmClose?: boolean
  subtitle?: string | React.ReactNode
  paperSx?: SxProps
}
interface IState {
  dialogShown: boolean
}

function SimpleModal(props: ISimpleModalProps) {
  const [state, setState] = React.useState<IState>({
    dialogShown: false,
  })

  const handleOpen = () => {
    if (props.onOpen) {
      props.onOpen()
    }
  }

  const handleClose = () => {
    if (props.confirmClose) {
      setState((state) => ({
        ...state,
        dialogShown: true,
      }))
    }
    if (
      (state.dialogShown && props.onClose) ||
      (props.onClose && !props.confirmClose)
    ) {
      props.onClose()
      setState((state) => ({
        ...state,
        dialogShown: false,
      }))
    }
  }

  const closeDialog = () => {
    setState((state) => ({
      ...state,
      dialogShown: false,
    }))
  }

  const onConfirm = () => {
    if (state.dialogShown && props.onClose) {
      props.onClose()
      setState((state) => ({
        ...state,
        dialogShown: false,
      }))
    }
  }

  return (
    <>
      {props.activator != null ? (
        <Box component="span" onClick={handleOpen} sx={{ display: 'inherit' }}>
          {props.activator}
        </Box>
      ) : null}

      <Dialog
        disableRestoreFocus={true}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={props.open}
        maxWidth="lg"
        scroll="paper"
        onClose={handleClose}
        id={props.id || ''}
        PaperProps={props.paperSx ? { sx: props.paperSx } : undefined}>
        <Box
          id="simple-modal-title"
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <DialogTitle>{props.title}</DialogTitle>
          <DialogActions>
            <IconButton
              onClick={handleClose}
              className="cp-modal-close"
              sx={{ color: 'text.primary' }}>
              <Icon>close</Icon>
            </IconButton>
          </DialogActions>
        </Box>
        <DialogContent
          id="simple-modal-description"
          sx={{ padding: 0 }}
          dividers={true}>
          <Typography
            variant="body1"
            sx={{
              paddingLeft: 3,
              paddingRight: 3,
              fontStyle: 'italic',
              fontWeight: 500,
              fontSize: '0.9rem',
            }}>
            {props.subtitle}
          </Typography>

          <div style={{ width: props.width }}>
            <Typography variant="subtitle1" id="simple-modal-description" />
            <div>{props.children}</div>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={state.dialogShown}
        onClose={closeDialog}
        onConfirm={onConfirm}
        title={translateWithUserLocale('warning.unsaved_changes')}
      />
    </>
  )
}

export default SimpleModal

interface IModalState {
  open: boolean
}

function Stateful(props: ISimpleModalProps) {
  const [state, setState] = React.useState<IModalState>({
    open: props.open,
  })

  React.useEffect(() => {
    setState((state) => ({ ...state, open: props.open }))
  }, [props.open])

  const open = () => {
    setState((state) => ({ ...state, open: true }))
    if (props.onOpen) {
      props.onOpen()
    }
  }

  const close = () => {
    setState((state) => ({ ...state, open: false }))
    if (props.onClose) {
      props.onClose()
    }
  }

  return (
    <SimpleModal
      {...props}
      open={state.open}
      onOpen={open}
      onClose={close}
      scroll="paper"
    />
  )
}

export const StatefulModal = Stateful
